/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useRef, useState, Suspense, useEffect } from 'react'
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import {
  Environment, OrbitControls, PerspectiveCamera, Html, useProgress
} from "@react-three/drei";

import {
  Switch, FormControlLabel, Slider, Button, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel,
  Select, MenuItem, IconButton, CircularProgress
} from '@mui/material';

import MouseIcon from '@mui/icons-material/Mouse';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import TuneIcon from '@mui/icons-material/Tune';
import SyncIcon from '@mui/icons-material/Sync';

const container = css`
  position: relative;
  display: flex;
  flex: 1 0 auto;
`

const controls = css`
  position: absolute;
  top: 0;
  left: 0;
  padding: 20px;
  color: white;
  background-color: rgba(0, 0, 0, 0.1);
  margin: 10px;
  border-radius: 8px;
  @media (max-width: 768px) {
    width: calc(100% - 60px)
  }
`

const row = css`
  display: flex;
  align-items: center;
  & >:first-of-type {
    min-width: 50%;
  }
`

const Model = (props) => {
  const gltf = useLoader(GLTFLoader, `./3d_models/${props.filename}`);

  const skinRef = useRef()
  // useFrame((state, delta) => (ref.current.rotation.y += 0.005))
  useFrame((state, delta) => (skinRef.current.rotation.y += 0.001))
  return (
    <primitive
      visible={props.visible}
      object={gltf.scene}
      scale={0.015}
      ref={skinRef}
    />
  );
};

const Loader = () => {
  const { progress } = useProgress()
  return (
    <Html center>
      <CircularProgress variant="determinate" value={progress} />
    </Html>
  )
}

const Box = (props) => {
  // This reference will give us direct access to the THREE.Mesh object
  const ref = useRef()

  // Set up state for the hovered and active state
  const [hovered, setHover] = useState(false)
  const [active, setActive] = useState(false)

  // Subscribe this component to the render-loop, rotate the mesh every frame
  useFrame((state, delta) => (ref.current.rotation.x += 0.005))
  useFrame((state, delta) => (ref.current.rotation.y += 0.005))

  useFrame(() => {
    // animate scale
    let scale = (ref.current.scale.x += ((active ? 0.1 : 0.05) - ref.current.scale.x) * 0.1)
    ref.current.scale.set(scale, scale, scale)
  })

  // Return the view, these are regular Threejs elements expressed in JSX
  return (
    <mesh
      {...props}
      ref={ref}
      scale={0.05}
      onClick={(event) => setActive(!active)}
      onPointerOver={(event) => setHover(true)}
      onPointerOut={(event) => setHover(false)}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color={hovered ? 'hotpink' : 'orange'} />
    </mesh>
  )
}

const Viewport = () => {

  useEffect(() => {
    loadJson()
  }, []);

  const loadJson = async () => {
    // fetch json from /public folder
    const response = await fetch('./models.json');
    const modelList = await response.json();
    console.log(modelList)
    setShowModels(false)
    setModels(modelList)
    setActiveModel(modelList[0].filename)
    setShowModels(true)
  }

  const handleSwitch = (e) => {
    setAltLighting(e.target.checked)
  }

  const handleCubes = (e) => {
    setShowCubes(e.target.checked)
  }

  const handleSlider = (e) => {
    setLightPos(e.target.value)
  }

  const handleAmbient = (e) => {
    setAmbient(e.target.value)
  }

  const handleToggleControls = (e) => {
    setShowControls(!showControls)
  }

  const handleModelSelect = (e) => {
    setActiveModel(e.target.value)
  }

  const handleRefetchJson = () => {
    console.log('refetching models...')
    loadJson()
  }

  const [altLighting, setAltLighting] = useState(false)
  const [showCubes, setShowCubes] = useState(false)
  const [lightPos, setLightPos] = useState(-10)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [ambient, setAmbient] = useState(0.1)
  const [showControls, setShowControls] = useState(true)
  const [models, setModels] = useState([])
  const [activeModel, setActiveModel] = useState('')
  const [showModels, setShowModels] = useState(true)
  // console.log('models', models)

  return (
    <div css={container}>
      <Canvas style={{ height: 'auto'}}>
        <Suspense fallback={<Loader />}>
          <PerspectiveCamera makeDefault
            position={[0, 0.15, 0.3]}
            near={0.01}
            far={100}
          />

          { showModels &&
            models.map((model, index) => {
              // only display the active model
              // console.log('activeModel:', activeModel)
              return (
                <Model
                  key={index}
                  visible={model.filename === activeModel}
                  filename={model.filename} />
              )
            })
          }

          {altLighting ?
            <Environment preset="sunset" />
            :
            <>
              <ambientLight intensity={ambient} />
              <pointLight position={[lightPos, 10, 10]} />
            </>
          }

          {
            showCubes &&
            <>
              <Box position={[-0.2, 0.05, -0.3]} />
              <Box position={[0.2, 0.05, -0.3]} />
            </>
          }

          <OrbitControls />
        </Suspense>
      </Canvas>

      { showControls &&
        <div css={controls}>
          <div css={css`
            display: flex;
            justify-content: space-between;
          `}>
            <FormControlLabel control={
              <Switch checked={showCubes} onChange={handleCubes} />
            } label="Show Cubes" />

            <FormControlLabel control={
              <Switch checked={altLighting} onChange={handleSwitch} />
            } label="Alt Lighting" />
          </div>

          {!altLighting &&
            <>
              <div css={row}>
                <p>Light Position</p>
                <Slider
                  max={10}
                  min={-10}
                  value={lightPos}
                  onChange={handleSlider}
                />
              </div>
              <div css={row}>
                <p style={{ marginTop: '0px'}}>
                  Ambient Intensity
                </p>
                <Slider
                  max={1}
                  min={0}
                  step={0.1}
                  value={ambient}
                  onChange={handleAmbient}
                />
              </div>
            </>
          }

          <div css={css`
            display: flex;
            align-items: center;
            margin: 10px 0;
          `}>
            <FormControl css={css`
              flex: 1 0 auto;
            `}>
              <InputLabel>Samples</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                value={activeModel}
                label="Samples"
                onChange={handleModelSelect}
              >
                {models.map((model, index) => {
                  return (
                    <MenuItem key={index} value={model.filename}>
                      {model.displayName}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>

            <IconButton aria-label="delete"
              css={css`
                flex: 0 0 auto;
                margin-left: 10px;
              `}
              onClick={handleRefetchJson}
            >
              <SyncIcon />
            </IconButton>
          </div>


          <div css={css`
            display: flex;
            justify-content: space-between;
          `}>
            <Button variant="text"
              startIcon={<MouseIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Instructions
            </Button>
            <Button variant="text"
              startIcon={<ArrowCircleUpIcon />}
              onClick={handleToggleControls}
            >
              Minimize
            </Button>
          </div>
        </div>
      }

      { !showControls &&
        <div css={controls}>
          <Button variant="text"
            startIcon={<TuneIcon />}
            onClick={handleToggleControls}
          >
            Show Controls
          </Button>
        </div>
      }

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      >
        <DialogTitle>Instructions</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <b>Move Camera:</b> click + drag
            <br/>
            <b>Pan:</b> shift + drag
            <br/>
            <b>Zoom:</b> scroll
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} autoFocus>Got It</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Viewport;
